import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, CreditCard } from 'lucide-react';
import { usePi } from '../../contexts/PiContext';
import Button from '../../components/UI/Button';
import PaymentMethods from '../../components/Payment/PaymentMethods';
import OrderSummary from '../../components/Payment/OrderSummary';
import SecurityNotice from '../../components/Payment/SecurityNotice';
import PaymentProcessor from '../../components/Payment/PaymentProcessor';
import PayPalCheckout from '../../components/Payment/PayPalCheckout';

const PaymentPage: React.FC = () => {
  const navigate = useNavigate();
  const { piBalance } = usePi();
  const [selectedMethod, setSelectedMethod] = useState('pi');
  const [loading, setLoading] = useState(false);
  const [showPayPalForm, setShowPayPalForm] = useState(false);

  const orderItems = [
    { name: 'Premium Monthly', price: selectedMethod === 'pi' ? 'π 19.99' : '$19.99' }
  ];

  const handlePaymentSuccess = (orderId?: string) => {
    setLoading(false);
    setShowPayPalForm(false);
    alert('Payment successful! Welcome to Pi Dating Premium!');
    navigate('/');
  };

  const handlePaymentError = (error: string) => {
    setLoading(false);
    setShowPayPalForm(false);
    alert(`Payment failed: ${error}`);
  };

  const { processPayment } = PaymentProcessor({
    method: selectedMethod,
    amount: 19.99,
    onSuccess: handlePaymentSuccess,
    onError: handlePaymentError
  });

  const handlePayment = async () => {
    if (selectedMethod === 'paypal') {
      setShowPayPalForm(true);
      return;
    }

    setLoading(true);
    await processPayment();
  };

  const handleMethodSelect = (method: string) => {
    setSelectedMethod(method);
    setShowPayPalForm(false);
  };

  return (
    <div className="min-h-screen">
      {/* Header */}
      <div className="p-4 flex items-center gap-3">
        <button
          onClick={() => navigate('/premium')}
          className="p-2 text-white hover:bg-white/10 rounded-full transition-colors"
        >
          <ArrowLeft size={20} />
        </button>
        <h1 className="text-2xl font-bold text-white">Payment</h1>
      </div>

      <div className="px-4 pb-8">
        {/* Order Summary */}
        <div className="mb-6">
          <OrderSummary 
            items={orderItems} 
            total={selectedMethod === 'pi' ? 'π 19.99' : '$19.99'} 
          />
        </div>

        {/* Payment Methods */}
        <div className="mb-6">
          <h3 className="font-semibold text-white mb-3">Payment Method</h3>
          <PaymentMethods
            selectedMethod={selectedMethod}
            onMethodSelect={handleMethodSelect}
            piBalance={piBalance}
          />
        </div>

        {/* PayPal Checkout Form */}
        {showPayPalForm && selectedMethod === 'paypal' && (
          <div className="mb-6">
            <PayPalCheckout
              amount={19.99}
              productType="Premium Monthly Subscription"
              onSuccess={handlePaymentSuccess}
              onError={handlePaymentError}
            />
          </div>
        )}

        {/* Payment Button - Only show if not PayPal or PayPal form is not shown */}
        {(!showPayPalForm || selectedMethod !== 'paypal') && (
          <Button
            onClick={handlePayment}
            disabled={loading}
            variant="gradient"
            size="lg"
            className="w-full mb-4"
          >
            {loading ? (
              'Processing...'
            ) : (
              <>
                <CreditCard size={20} className="mr-2" />
                {selectedMethod === 'paypal' ? 'Continue to PayPal' :
                 selectedMethod === 'pi' ? 'Pay π 19.99' : 'Pay $19.99'}
              </>
            )}
          </Button>
        )}

        {/* Security Notice */}
        <SecurityNotice />

        {/* PayPal Badge */}
        {selectedMethod === 'paypal' && (
          <div className="mt-4 text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 rounded-full">
              <span className="text-white text-sm">Secured by</span>
              <div className="bg-blue-600 px-3 py-1 rounded text-white font-bold text-sm">
                PayPal
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentPage;